import * as React from 'react';

import { enumServiceStatus } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, Col, Menu, MenuItem, Row, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { MoreHorizontal } from 'lucide-react';

import { useServiceTransitionMutation } from '../../hooks/mutations/useServiceTransitionMutation';

import { ServiceFormState } from '~/modules/transportation/types';

type ServiceStatusActionsProps = {
  service: ServiceFormState & { id: string };
  onSuccess: () => void;
  onClose: () => void;
};

const TERMINAL_STATUSES = [
  enumServiceStatus.Voided,
  enumServiceStatus.Completed,
  enumServiceStatus.Failed,
] as string[];

export const ServiceStatusActions = ({
  service,
  onSuccess,
  onClose,
}: ServiceStatusActionsProps) => {
  const t = useTranslations();
  const toast = useToast();

  const { mutateAsync: transitionService, isPending: isTransitioning } =
    useServiceTransitionMutation();

  const anyPending = isTransitioning;

  const handleAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      await action();
      toast.success(successMessage);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.servicesView.form.messages.error,
      );
    }
  };

  const handlePlan = () =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.Planned,
        }),
      t.servicesView.form.messages.planSuccess,
    );

  const handleStart = () =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.InProgress,
        }),
      t.servicesView.form.messages.startSuccess,
    );

  const handleComplete = () =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.Completed,
        }),
      t.servicesView.form.messages.completeSuccess,
    );

  const handleVoid = (reason: string) =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.Voided,
          notes: reason,
        }),
      t.servicesView.form.messages.voidSuccess,
    );

  const handleFail = (reason: string) =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.Failed,
          notes: reason,
        }),
      t.servicesView.form.messages.failSuccess,
    );

  const handleRevertToDraft = () =>
    handleAction(
      () =>
        transitionService({
          id: service.id,
          status: enumServiceStatus.Draft,
        }),
      'Service reverted to Draft',
    );

  const canPlan =
    !!service.accountAddressId &&
    (service.assetQuantities?.some((aq) => aq.quantity > 0) ?? false);

  const getPrimaryAction = () => {
    switch (service.status) {
      case enumServiceStatus.Draft:
        return {
          label: t.servicesView.form.buttons.plan,
          color: 'primary' as const,
          variant: 'solid' as const,
          onPress: handlePlan,
          isDisabled: !canPlan,
        };
      case enumServiceStatus.Planned:
        return {
          label: t.servicesView.form.buttons.start,
          color: 'primary' as const,
          variant: 'solid' as const,
          onPress: handleStart,
        };
      case enumServiceStatus.InProgress:
        return {
          label: t.servicesView.form.buttons.complete,
          color: 'success' as const,
          variant: 'solid' as const,
          onPress: handleComplete,
        };
      default:
        return null;
    }
  };

  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];
    const status = service.status as string;

    if (status === enumServiceStatus.Planned) {
      items.push({
        label: 'Revert to Draft',
        onAction: handleRevertToDraft,
      });
    }

    const canVoid = !TERMINAL_STATUSES.includes(status);
    if (canVoid) {
      items.push({
        label: t.servicesView.form.buttons.void,
        className: 'text-failure',
        secondStep: ({ close }) => (
          <ReasonInputStep
            title={t.servicesView.form.reasonPrompts.voidReason}
            placeholder={t.servicesView.form.reasonPrompts.reasonPlaceholder}
            required
            onConfirm={(reason) => {
              close();
              handleVoid(reason);
            }}
            onCancel={close}
          />
        ),
      });
    }

    const canFail = status === enumServiceStatus.InProgress;
    if (canFail) {
      items.push({
        label: t.servicesView.form.buttons.fail,
        className: 'text-failure',
        secondStep: ({ close }) => (
          <ReasonInputStep
            title={t.servicesView.form.reasonPrompts.failReason}
            placeholder={t.servicesView.form.reasonPrompts.reasonPlaceholder}
            required
            onConfirm={(reason) => {
              close();
              handleFail(reason);
            }}
            onCancel={close}
          />
        ),
      });
    }

    return items;
  };

  const primaryAction = getPrimaryAction();
  const menuItems = getMenuItems();

  return (
    <>
      {menuItems.length > 0 && (
        <Menu
          trigger={
            <Button
              icon={<MoreHorizontal />}
              variant="outline"
              isDisabled={anyPending}
            />
          }
          items={menuItems}
        />
      )}
      {primaryAction && (
        <Button
          color={primaryAction.color}
          variant={primaryAction.variant}
          onPress={primaryAction.onPress}
          isPending={anyPending}
          isDisabled={anyPending || (primaryAction.isDisabled ?? false)}
        >
          {primaryAction.label}
        </Button>
      )}
    </>
  );
};

type ReasonInputStepProps = {
  title: string;
  placeholder: string;
  required: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
};

const ReasonInputStep = ({
  title,
  placeholder,
  required,
  onConfirm,
  onCancel,
}: ReasonInputStepProps) => {
  const t = useTranslations();
  const [reason, setReason] = React.useState('');

  return (
    <Col gap3 className="@ReasonInputStep">
      <div className="text-sm font-medium">{title}</div>
      <TextField
        value={reason}
        onChange={setReason}
        placeholder={placeholder}
        aria-label={title}
      />
      <Row gap2>
        <Button variant="outline" color="neutral" onPress={onCancel}>
          {t.servicesView.form.buttons.cancel}
        </Button>
        <Button
          variant="solid"
          color="primary"
          isDisabled={required && !reason.trim()}
          onPress={() => onConfirm(reason.trim())}
        >
          {t.servicesView.form.buttons.confirm}
        </Button>
      </Row>
    </Col>
  );
};
